<script setup>
/**
 * One internal note, as a chat bubble.
 *
 * The feed is chronological, so a reply sits where it was written rather than
 * underneath what it answers — the bubble quotes the name instead. Alignment and
 * grouping are decided by `mergeCaseFeed`, which is the only thing that can see
 * the entry before this one.
 *
 * An archived comment stays here as a tombstone rather than disappearing: its
 * text is genuinely gone, but the feed does not silently renumber itself.
 *
 * Only the author may edit — an administrator moderating the thread cannot
 * reword somebody else's words — while removing is open to the author or an
 * administrator. That is the server's rule; the controls here just stop offering
 * an action that would be refused.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CaseCommentComposer from './CaseCommentComposer.vue';
import {
  ACTOR_TYPE,
  actorName,
  lastActivityLabel,
} from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  comment: {
    type: Object,
    required: true,
  },
  // The signed-in agent, for the authorship rules.
  currentUserId: {
    type: [Number, String],
    default: null,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  // Replies are one level deep, so a reply offers no reply control of its own.
  isReply: {
    type: Boolean,
    default: false,
  },
  // Which side of the feed this note sits on: `end` is the signed-in agent's
  // own, in the same amber the inbox tints a private note.
  align: {
    type: String,
    default: 'start',
    validator: value => ['start', 'end'].includes(value),
  },
  // The previous entry was the same author, moments ago: same turn, so no
  // second avatar and no second name.
  isGrouped: {
    type: Boolean,
    default: false,
  },
  // Whom this note is answering, when the parent is on the page.
  replyToName: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['reply', 'edit', 'archive']);

const { t } = useI18n();

const isEditing = ref(false);

const author = computed(() => props.comment.author || {});

const isBot = computed(() => author.value.type === 'AgentBot');

// A rule's note is signed with the rule that wrote it. The badge says the note was made by
// a rule rather than typed by someone, which is what the reader needs before deciding
// whether to argue with it.
const isAutomation = computed(
  () => author.value.type === ACTOR_TYPE.AUTOMATION_RULE
);

const authorName = computed(() => actorName(props.comment.author, t));

// Only a person can be the author, so only a person gets the edit and remove controls: a
// rule's note carries the rule's id, and rule 7 is not agent 7.
const isAuthor = computed(
  () =>
    author.value.type === ACTOR_TYPE.USER &&
    props.currentUserId != null &&
    String(author.value.id) === String(props.currentUserId)
);

const canEdit = computed(() => !props.comment.archived && isAuthor.value);

const canArchive = computed(
  () => !props.comment.archived && (isAuthor.value || props.isAdmin)
);

const isOwn = computed(() => props.align === 'end');

const timestamp = computed(() => lastActivityLabel(props.comment.createdAt));

// A tombstone keeps the neutral surface whoever wrote it: an amber bubble with
// no words in it reads as a colour with no meaning.
const bubbleClass = computed(() => {
  if (props.comment.archived) {
    return 'bg-n-solid-2 border border-n-weak italic text-n-slate-10';
  }
  return isOwn.value
    ? 'bg-n-solid-amber text-n-amber-12 border border-n-amber-12/10'
    : 'bg-n-solid-2 text-n-slate-12 border border-n-weak';
});

const onEdit = content => {
  isEditing.value = false;
  if (content === props.comment.content) return;
  emit('edit', { comment: props.comment, content });
};
</script>

<template>
  <div
    class="flex items-start gap-2 group"
    :class="isOwn ? 'ltr:flex-row-reverse rtl:flex-row' : ''"
    :data-testid="`case-comment-${comment.id}`"
    :data-archived="comment.archived ? 'true' : 'false'"
  >
    <Avatar
      v-if="!isGrouped"
      :name="authorName"
      :src="author.thumbnail"
      :size="24"
    />
    <span v-else class="size-6 shrink-0" aria-hidden="true" />

    <div
      class="flex flex-col gap-1 min-w-0 max-w-[85%]"
      :class="isOwn ? 'items-end' : 'items-start'"
    >
      <div v-if="!isGrouped" class="flex flex-wrap items-baseline gap-2">
        <span class="text-xs text-n-slate-11">{{ authorName }}</span>
        <span
          v-if="isBot"
          class="px-1.5 py-0.5 text-xs rounded text-n-slate-11 bg-n-alpha-2"
          data-testid="case-comment-bot"
        >
          {{ t('SUPPORT_CASES.DETAIL.THREAD.BOT') }}
        </span>
        <span
          v-else-if="isAutomation"
          class="px-1.5 py-0.5 text-xs rounded text-n-slate-11 bg-n-alpha-2"
          data-testid="case-comment-automation"
        >
          {{ t('SUPPORT_CASES.DETAIL.THREAD.AUTOMATION') }}
        </span>
        <span class="text-xs text-n-slate-10">{{ timestamp }}</span>
        <span
          v-if="comment.edited && !comment.archived"
          class="text-xs text-n-slate-10"
          data-testid="case-comment-edited"
        >
          {{ t('SUPPORT_CASES.DETAIL.THREAD.EDITED') }}
        </span>
      </div>

      <CaseCommentComposer
        v-if="isEditing"
        :initial-content="comment.content || ''"
        :show-note="false"
        cancellable
        autofocus
        :disabled="disabled"
        :submit-label="t('SUPPORT_CASES.DETAIL.THREAD.SAVE')"
        :data-testid="`case-comment-edit-${comment.id}`"
        @submit="onEdit"
        @cancel="isEditing = false"
      />

      <div
        v-else
        class="px-3 py-2 min-w-0 rounded-xl"
        :class="bubbleClass"
        :data-testid="`case-comment-body-${comment.id}`"
      >
        <span
          v-if="replyToName"
          class="block text-xs italic truncate text-n-slate-11"
          :data-testid="`case-comment-reply-quote-${comment.id}`"
        >
          {{
            t('SUPPORT_CASES.DETAIL.THREAD.REPLYING_TO', { name: replyToName })
          }}
        </span>

        <p
          v-if="comment.archived"
          class="mb-0 text-sm"
          data-testid="case-comment-tombstone"
        >
          {{ t('SUPPORT_CASES.DETAIL.THREAD.REMOVED') }}
        </p>

        <p v-else class="mb-0 text-sm whitespace-pre-wrap">
          {{ comment.content }}
        </p>
      </div>

      <!-- Hover is polish, not a gate: the controls stay in the document so a
           keyboard reaches them, and focus-within brings them back into view.
           Every note offers the same three words, so the visible label alone
           leaves a screen reader with a list of identical controls — the
           accessible name says whose note. -->
      <div
        v-if="!isEditing"
        class="flex items-center gap-1 transition-opacity opacity-0 group-hover:opacity-100 focus-within:opacity-100"
        :class="isOwn ? 'ltr:justify-end rtl:justify-start' : ''"
      >
        <Button
          v-if="!isReply && !comment.archived"
          variant="link"
          color="slate"
          size="xs"
          :label="t('SUPPORT_CASES.DETAIL.THREAD.REPLY')"
          :aria-label="
            t('SUPPORT_CASES.DETAIL.THREAD.REPLY_ARIA', { name: authorName })
          "
          :disabled="disabled"
          :data-testid="`case-comment-reply-${comment.id}`"
          @click="emit('reply', comment)"
        />
        <Button
          v-if="canEdit"
          variant="link"
          color="slate"
          size="xs"
          :label="t('SUPPORT_CASES.DETAIL.THREAD.EDIT')"
          :aria-label="
            t('SUPPORT_CASES.DETAIL.THREAD.EDIT_ARIA', { time: timestamp })
          "
          :disabled="disabled"
          :data-testid="`case-comment-edit-button-${comment.id}`"
          @click="isEditing = true"
        />
        <Button
          v-if="canArchive"
          variant="link"
          color="slate"
          size="xs"
          :label="t('SUPPORT_CASES.DETAIL.THREAD.REMOVE')"
          :aria-label="
            t('SUPPORT_CASES.DETAIL.THREAD.REMOVE_ARIA', { name: authorName })
          "
          :disabled="disabled"
          :data-testid="`case-comment-remove-${comment.id}`"
          @click="emit('archive', comment)"
        />
      </div>
    </div>
  </div>
</template>
